/**
 * Bitmaps drawn on leaflet pages. The branch stores each bitmap once, and pages
 * refer to it by its SHA-256; the editor draws it from a local object URL.
 */

export const bitmapTypes = ["image/png", "image/jpeg", "image/webp"] as const;
export type BitmapType = (typeof bitmapTypes)[number];

/** The server rejects larger bitmaps. */
export const MAXIMUM_BITMAP_BYTES = 20 * 1024 * 1024;

const REFERENCE = /^sha256:([0-9a-f]{64})$/;

export function isBitmapType(value: unknown): value is BitmapType {
  return bitmapTypes.some((type) => type === value);
}

/** How pages refer to a bitmap: `sha256:` and its hexadecimal digest. */
export function isBitmapReference(value: unknown): value is string {
  return typeof value === "string" && REFERENCE.test(value);
}

/** The hexadecimal digest that names a bitmap in API paths. */
export function bitmapDigest(reference: string): string {
  const digest = REFERENCE.exec(reference)?.[1];
  if (digest === undefined) throw new TypeError("Invalid bitmap reference");
  return digest;
}

interface Entry {
  readonly blob: Blob;
  readonly url: string;
}

/** A leaflet's bitmaps, by reference, with object URLs that last until `destroy`. */
export class LeafletBitmaps {
  private readonly entries = new Map<string, Entry>();
  private readonly references = new Map<string, string>();

  /** Adds a bitmap if it is new, returning its reference. */
  public async add(blob: Blob): Promise<string> {
    if (!isBitmapType(blob.type)) throw new TypeError("Images must be PNG, JPEG or WebP.");
    const digest = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
    const reference = `sha256:${[...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("")}`;
    if (!this.entries.has(reference)) {
      const url = URL.createObjectURL(blob);
      this.entries.set(reference, { blob, url });
      this.references.set(url, reference);
    }
    return reference;
  }

  public blob(reference: string): Blob {
    return this.entry(reference).blob;
  }

  public url(reference: string): string {
    return this.entry(reference).url;
  }

  /** The reference of a bitmap drawn from `url`. */
  public reference(url: string): string {
    const reference = this.references.get(url);
    if (reference === undefined) throw new Error("The leaflet editor drew an unknown image.");
    return reference;
  }

  public destroy(): void {
    for (const { url } of this.entries.values()) URL.revokeObjectURL(url);
    this.entries.clear();
    this.references.clear();
  }

  private entry(reference: string): Entry {
    const entry = this.entries.get(reference);
    if (entry === undefined) throw new Error("This leaflet uses an image that is not loaded.");
    return entry;
  }
}
